import { computed, inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@auth/services/frontend/auth.service';
import { ROUTEPERMISSIONS } from '@sharedEnums/route-permissions';
import { hideitemNavigation } from '@utils/hide-item-navigation/hide-item-navigation';

@Injectable({
    providedIn: 'root',
})
export class GlobalNavigationGuardService {
    private router = inject(Router);
    private route = inject(ActivatedRoute);
    private authService = inject(AuthService);

    loggedInUser = computed(() => this.authService.loggedInUserSignal());

    constructor() {
        // Escucha todos los eventos de navegación para depuración
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                const baseUrl = event.urlAfterRedirects;
                const checkAccessToRouteResult = this.checkAccess(baseUrl);

                if (
                    baseUrl === '/sign-out' ||
                    baseUrl === '/errors/error-404' ||
                    this.loggedInUser().id === 'Not loggedIn'
                ) {
                    return;
                }

                if (
                    !checkAccessToRouteResult &&
                    this.loggedInUser().permissions.length !== 0
                ) {
                    this.router.navigateByUrl('errors/error-403');
                    return;
                }

                if (this.loggedInUser().permissions.length === 0) {
                    this.router.navigateByUrl('errors/error-403');
                }
            }
        });
    }

    checkAccess(originalUrl: string): boolean {
        const routeUrl = this.getRouteFromMap(originalUrl);
        const routePermissionObject = ROUTEPERMISSIONS[routeUrl];

        if (!routePermissionObject) {
            return false;
        }

        const shouldHide = hideitemNavigation({
            permissions: routePermissionObject.permissions,
            user: this.loggedInUser(),
            mode: routePermissionObject.mode,
        });

        if (shouldHide) {
            return false;
        }

        return true;
    }

    getRouteFromMap(url: string): string {
        const isInMap = ROUTEPERMISSIONS[url];

        if (isInMap) {
            return url;
        }

        return this.getNormalizedUrlFromRoutePermissions(url);
    }

    getNormalizedUrlFromRoutePermissions(url: string): string {
        const baseUrl = url.split('?')[0];

        const normalizedUrl = baseUrl
            .split('/')
            .map((segment) => segment.split('?')[0])
            .join('/');

        for (const route of Object.keys(ROUTEPERMISSIONS)) {
            const regex = new RegExp(
                '^' +
                    route.replace(/:[a-zA-Z0-9-_]+/g, '([a-zA-Z0-9-_]+)') +
                    '$'
            );

            if (regex.test(normalizedUrl)) {
                return route.replace(
                    /:[a-zA-Z0-9-_]+/g,
                    (param) => `:${param.slice(1)}`
                );
            }
        }

        return normalizedUrl;
    }
}
